import { useEffect, useState } from "react";
import { api } from "../../helpers/apiCore";
import { endpoints } from "../../helpers/Endpoints";
import { Alert, Button, Spinner, Modal } from "react-bootstrap";
import { User } from "../../types";
import backBtn from "../../assets/icons/arrow_left.svg";
import errorInfoIcon from "../../assets/icons/error_info.svg";
import FormInput from "../../components/FormInput";

interface UserDetailsProps {
	userId: string;
	onHide: () => void;
}

interface ImagePreviewProps {
	src: string;
	alt: string;
	show: boolean;
	onHide: () => void;
}

const ImagePreviewModal = ({ src, alt, show, onHide }: ImagePreviewProps) => (
	<Modal show={show} onHide={onHide} size="lg" centered>
		<Modal.Header closeButton>
			<Modal.Title>{alt}</Modal.Title>
		</Modal.Header>
		<Modal.Body className="text-center">
			<img 
				src={src} 
				alt={alt} 
				style={{ 
					maxWidth: '100%', 
					maxHeight: '70vh', 
					objectFit: 'contain' 
				}} 
			/>
		</Modal.Body>
		<Modal.Footer>
			<Button variant="secondary" onClick={onHide}>
				Close
			</Button>
			<Button 
				variant="primary" 
				onClick={() => window.open(src, '_blank')}
			>
				Open in New Tab
			</Button>
		</Modal.Footer>
	</Modal>
);

interface PreviewableImageProps {
	src: string | undefined;
	alt: string;
	label: string;
	onPreview: (src: string, alt: string) => void;
}

const PreviewableImage = ({ src, alt, label, onPreview }: PreviewableImageProps) => {
	const [imageError, setImageError] = useState(false);
	const [imageLoading, setImageLoading] = useState(true);

	return (
		<div className="mb-4">
			<label className="custom-label">{label}</label>
			<div className="image-preview-container" style={{ position: 'relative', display: 'inline-block' }}>
				{src && !imageError ? (
					<>
						<img 
							src={src} 
							alt={alt} 
							className="image-urls"
							style={{ 
								cursor: 'pointer',
								opacity: imageLoading ? 0.5 : 1,
								transition: 'opacity 0.3s ease'
							}}
							onLoad={() => setImageLoading(false)}
							onError={() => {
								setImageError(true);
								setImageLoading(false);
							}}
							onClick={() => onPreview(src, `${label} - ${alt}`)}
						/>
						
						<div 
							className="image-overlay"
							style={{
								position: 'absolute',
								top: '8px',
								right: '8px',
								background: 'rgba(0,0,0,0.7)',
								borderRadius: '4px',
								padding: '4px 8px',
								cursor: 'pointer',
								opacity: 0,
								transition: 'opacity 0.3s ease'
							}}
							onClick={() => onPreview(src, `${label} - ${alt}`)}
						>
							<span style={{ color: 'white', fontSize: '12px' }}>🔍 View</span>
						</div>

						{imageLoading && (
							<div style={{ 
								position: 'absolute', 
								top: '50%', 
								left: '50%', 
								transform: 'translate(-50%, -50%)' 
							}}>
								<Spinner animation="border" size="sm" />
							</div>
						)}
					</>
				) : (
					<div 
						className="image-placeholder"
						style={{
							width: '200px',
							height: '150px',
							border: '2px dashed #ccc',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							color: '#666',
							fontSize: '14px'
						}}
					>
						{imageError ? 'Failed to load image' : 'No image available'}
					</div>
				)}
			</div>
		</div>
	);
};

const UserDetails = ({ userId, onHide }: UserDetailsProps) => {
	const [userDetail, setUserDetail] = useState<User | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const [showAlert, setShowAlert] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [showVerification, setShowVerification] = useState(false);
	const [successMessage, setSuccessMessage] = useState('');
	const [previewImage, setPreviewImage] = useState<{src: string; alt: string} | null>(null);

	const handleApiCall = async (apiCall: () => Promise<any>, successCallback?: () => void) => {
		setIsLoading(true);
		try {
			api.setAuthorization(api.getLoggedInUser());
			const response = await apiCall();
			successCallback?.();
			return response;
		} catch (error: any) {
			const message = error?.response?.data?.message || 'Operation failed, please try again';
			setError(message);
			setShowAlert(true);
		} finally {
			setIsLoading(false);
		}
	};

	const fetchUser = async () => {
		const response = await handleApiCall(
			() => api.get(endpoints.getUserData(userId), {})
		);

		if (response?.data) {
			setUserDetail(response.data);
		}
	};

	const toggleUserStatus = (isDisabling: boolean) => 
		handleApiCall(
			() => api.create(isDisabling ? endpoints.disableUser(userId) : endpoints.enableUser(userId), {}),
			fetchUser
		);

	const approveKYC = async () => {
		const confirmed = window.confirm("Are you sure you want to approve this user's KYC?");
		if (!confirmed) return;

		const response = await handleApiCall(
			() => api.create(endpoints.approveUserKyc(userId), {})
		);
		if (response?.data) {
			setUserDetail(prev => prev ? { ...prev, kyc_verified: true } : null);
			setSuccessMessage('KYC approved successfully');
			setShowAlert(true);
		}
	};

	const rejectKYC = async () => {
		const confirmed = window.confirm("Are you sure you want to reject this user's KYC?");
		if (!confirmed) return;

		const response = await handleApiCall(
			() => api.create(endpoints.rejectUserKyc(userId), {}),
			fetchUser
		);
		if (response?.data) {
			setUserDetail(prev => prev ? { ...prev, kyc_verified: false } : null);
			setSuccessMessage('KYC rejected successfully');
			setShowAlert(true);
		}
	};

	const handleImagePreview = (src: string, alt: string) => {
		setPreviewImage({ src, alt });
	};

	const closeImagePreview = () => {
		setPreviewImage(null);
	};

	useEffect(() => {
		if (userId) fetchUser();
	}, [userId]);

	useEffect(() => {
		if (!showAlert) return;
		
		const timeout = setTimeout(() => {
			setShowAlert(false);
			setError(null);
			setSuccessMessage('');
		}, 4000);

		return () => clearTimeout(timeout);
	}, [showAlert]);

	const userFields = [
		{ label: "Address", value: userDetail?.address},
		{ label: "Nin", value: userDetail?.nin },
		{ label: "Bvn", value: userDetail?.bvn },
		{ label: "Date of Birth", value: userDetail?.date_of_birth ?? "-" },
	];

	const accountFields = [
		{ label: "Account Number", value: userDetail?.accountDetails?.account_number},
		{ label: "Account Type", value: userDetail?.accountDetails?.account_type },
		{ label: "Balance", value: userDetail?.accountDetails?.balance },
	];

	const isUserDisabled = userDetail?.disabled;

	const documentImages = [
		{
			src: userDetail?.image_url || undefined,
			alt: "User Profile",
			label: "Profile Image"
		},
		{
			src: userDetail?.idcard_url || undefined,
			alt: "NIN ID Card",
			label: "NIN ID Card"
		},
		{
			src: userDetail?.utility_bill_url || undefined,
			alt: "Utility Bill",
			label: "Utility Bill"
		}
	];

	return (
		<>
			<div className="modal-backdrop" onClick={onHide}></div>

			<div className="modal-container">
				<div className="book-details-body">
					<div className="custom-modal-header">
						<div className="custom-back">
							<img src={backBtn} alt="back" onClick={onHide} />
							<h4>User Details</h4>
						</div>

						<Button 
							className="edit-user-status" 
							onClick={() => setShowVerification(!showVerification)}
						>
							{showVerification ? "Cancel" : "Edit Status"}
						</Button>
					</div>

					{isLoading ? (
						<div className="spinner-div">
							<Spinner animation="border" size="sm" role="status" />
						</div>
					) : (
						<div className="custom-modal-body">
							<div className="detail-section">
								<div className="documents-section mb-4">
									<h5 className="section-title mb-3">Documents & Images</h5>
									<div className="documents-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
										{documentImages.map(({ src, alt, label }) => (
											<PreviewableImage
												key={label}
												src={src}
												alt={alt}
												label={label}
												onPreview={handleImagePreview}
											/>
										))}
									</div>
								</div>

								<div className="user-info-section mb-5">
									<h5 className="section-title mb-3">Personal Information</h5>
									{userFields?.map(({ label, value }) => (
										<FormInput
											key={label}
											label={label}
											name={label.toLowerCase().replace(' ', '-')}
											className="detail-input"
											value={value ?? ""}
											readOnly
										/>
									))}
								</div>

								<div className="user-info-section mb-4 mt-5">
									<h5 className="section-title mb-3">Account Information</h5>
									{accountFields?.map(({ label, value }) => (
										<FormInput
											key={label}
											label={label}
											name={label.toLowerCase().replace(' ', '-')}
											className="detail-input"
											value={value ?? ""}
											readOnly
										/>
									))}
								</div>

								{showVerification && (
									<div className="verification text-center mb-4">
										<h5 className="section-title mb-3">Status Management</h5>
										<div className="verification-flex mb-3">
											<label>User Status</label>
											<p className={isUserDisabled ? "user-disabled" : "user-enabled"}>
												{isUserDisabled ? "Disabled" : "Enabled"}
											</p>
										</div>

										{showAlert && (error || successMessage) && (
											<Alert variant={error ? 'danger' : 'success'} className="alert-error text-center mt-2">
												{error && <img src={errorInfoIcon} alt="error" />}
												{error || successMessage}
											</Alert>
										)}

										<Button 
											className={`${isUserDisabled ? 'enable-btn' : 'disable-btn'} custom-button`}
											onClick={() => toggleUserStatus(!isUserDisabled)}
											disabled={isLoading}
										>
											{isLoading && <Spinner animation="border" size="sm" role="status" />}
											{isUserDisabled ? 'Enable' : 'Disable'}
										</Button>
									</div>
								)}

								<div className="verification mt-5">
									<h5 className="section-title mb-3">KYC Verification</h5>
									<div className="verification-flex mb-3">
										<label>KYC Status</label>
										<p className={userDetail?.kyc_verified ? "user-enabled" : "user-disabled"}>
											{userDetail?.kyc_verified ? "Verified" : "Not Verified"}
										</p>
									</div>

									{showAlert && (error || successMessage) && (
										<Alert variant={error ? 'danger' : 'success'} className="alert-error text-center mt-2">
											{error && <img src={errorInfoIcon} alt="error" />}
											{error || successMessage}
										</Alert>
									)}

									{userDetail?.kyc_submitted && !userDetail?.kyc_verified && (
										<>
											<Button 
												className="approve-kyc-btn custom-button"
												onClick={approveKYC}
												disabled={isLoading}
											>
												{isLoading && <Spinner animation="border" size="sm" role="status" />}
												Approve KYC
											</Button>

											<Button 
												className="reject-kyc-btn custom-button mt-2"
												onClick={rejectKYC}
												disabled={isLoading}
											>
												{isLoading && <Spinner animation="border" size="sm" role="status" />}
												Reject KYC
											</Button>
										</>
									)}
								</div>
							</div>
						</div>
					)}
				</div>
			</div>

			<ImagePreviewModal
				src={previewImage?.src || ''}
				alt={previewImage?.alt || ''}
				show={!!previewImage}
				onHide={closeImagePreview}
			/>

			<style>{`
				.image-preview-container:hover .image-overlay {
					opacity: 1 !important;
				}
				
				.image-urls {
					border-radius: 8px;
					box-shadow: 0 2px 8px rgba(0,0,0,0.1);
					transition: transform 0.2s ease, box-shadow 0.2s ease;
				}
				
				.image-urls:hover {
					transform: scale(1.02);
					box-shadow: 0 4px 16px rgba(0,0,0,0.15);
				}
				
				.section-title {
					color: #333;
					font-weight: 600;
					border-bottom: 2px solid #f0f0f0;
					padding-bottom: 8px;
				}
			`}</style>
		</>
	);
};

export default UserDetails;